import { useState, useRef, useCallback } from 'react';
import { Upload, X, Image, GripVertical, Star, AlertCircle, CheckCircle, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ImageSearchModal from './ImageSearchModal';
import ImageCropModal from './ImageCropModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_IMAGES = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = [
  'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/bmp',
  'image/heic', 'image/heif', 'image/avif'
];

const SortableImage = ({ id, url, isMain, onRemove, onSetMain }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 1,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group ${isDragging ? 'shadow-xl' : ''}`}
    >
      <div className={`relative w-24 h-24 rounded-lg overflow-hidden border-2 ${isMain ? 'border-[#0047AB] ring-2 ring-[#0047AB]/30' : 'border-slate-200'}`}>
        <img src={url} alt="Product" className="w-full h-full object-cover" />
        
        {/* Main badge */}
        {isMain && (
          <div className="absolute top-1 left-1 bg-[#0047AB] text-white text-[10px] px-1.5 py-0.5 rounded font-semibold flex items-center gap-0.5">
            <Star className="w-2.5 h-2.5 fill-current" />
            Main
          </div>
        )}
        
        {/* Drag handle */}
        <div
          {...attributes}
          {...listeners}
          className="absolute bottom-1 left-1 p-1 bg-black/50 rounded cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <GripVertical className="w-3 h-3 text-white" />
        </div>
        
        {/* Actions */}
        <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {!isMain && (
            <button
              type="button"
              onClick={() => onSetMain(id)}
              className="p-1 bg-white rounded shadow hover:bg-slate-100"
              title="Set as main image"
            >
              <Star className="w-3 h-3 text-amber-500" />
            </button>
          )}
          <button
            type="button"
            onClick={() => onRemove(id)}
            className="p-1 bg-white rounded shadow hover:bg-red-50"
            title="Remove image"
          >
            <X className="w-3 h-3 text-red-500" />
          </button>
        </div>
      </div>
    </div>
  );
};

const MultiImageUploader = ({ value = [], mainImage = '', onChange, onMainImageChange, disabled }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showSearch, setShowSearch] = useState(false);
  const fileInputRef = useRef(null);

  // Crop modal state: queue of files to crop, current preview src + filename
  const [cropQueue, setCropQueue] = useState([]); // remaining files awaiting crop decision
  const [cropPreview, setCropPreview] = useState(null); // { src, fileName }
  const [pendingUploads, setPendingUploads] = useState([]); // files that have been finalized (cropped or skipped) awaiting upload
  
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Create image items with unique IDs
  const imageItems = value.map((url, index) => ({
    id: `img-${index}-${url.slice(-10)}`,
    url,
    isMain: url === mainImage
  }));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = imageItems.findIndex(item => item.id === active.id);
      const newIndex = imageItems.findIndex(item => item.id === over.id);
      
      const newOrder = arrayMove(value, oldIndex, newIndex);
      onChange(newOrder);
      
      // If main image was first and got moved, update main to new first
      if (oldIndex === 0 && mainImage === value[0]) {
        onMainImageChange(newOrder[0]);
      }
    }
  };

  const validateFile = (file) => {
    // Browsers sometimes return empty file.type for HEIC; fall back to extension check
    const ext = (file.name || '').split('.').pop()?.toLowerCase() || '';
    const allowedExts = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'heic', 'heif', 'avif'];
    const typeOk = !file.type || ALLOWED_TYPES.includes(file.type) || file.type.startsWith('image/');
    const extOk = allowedExts.includes(ext);
    if (!typeOk && !extOk) {
      return 'Invalid file type. Please use JPG, PNG, WebP, GIF, HEIC, or AVIF';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File too large. Maximum size is 5MB';
    }
    return null;
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const token = localStorage.getItem('admin_token');
    const response = await axios.post(`${API}/upload-image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${token}`
      }
    });
    
    return `${BACKEND_URL}${response.data.url}`;
  };

  const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const openCropForFile = async (file) => {
    const src = await fileToDataUrl(file);
    setCropPreview({ src, fileName: file.name });
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remainingSlots = MAX_IMAGES - value.length;
    if (remainingSlots <= 0) {
      toast.error(`Maximum ${MAX_IMAGES} images allowed`);
      return;
    }

    const filesToProcess = files.slice(0, remainingSlots);

    // Validate all files first
    for (const file of filesToProcess) {
      const error = validateFile(file);
      if (error) {
        toast.error(`${file.name}: ${error}`);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
    }

    // Clear input so same file can be re-selected later
    if (fileInputRef.current) fileInputRef.current.value = '';

    // Queue: take first file → open crop modal; rest wait
    const [first, ...rest] = filesToProcess;
    setCropQueue(rest);
    setPendingUploads([]);
    await openCropForFile(first);
  };

  const finalizeUploads = async (files) => {
    if (!files.length) return;
    setUploading(true);
    setUploadProgress(0);
    const newUrls = [];
    let completed = 0;
    try {
      for (const file of files) {
        const url = await uploadFile(file);
        newUrls.push(url);
        completed++;
        setUploadProgress(Math.round((completed / files.length) * 100));
      }
      const updatedImages = [...value, ...newUrls];
      onChange(updatedImages);
      if (!mainImage && updatedImages.length > 0) {
        onMainImageChange(updatedImages[0]);
      }
      toast.success(`${newUrls.length} image(s) uploaded successfully`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload images');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const proceedQueue = async (nextPending) => {
    setCropPreview(null);
    if (cropQueue.length > 0) {
      const [next, ...rest] = cropQueue;
      setCropQueue(rest);
      setPendingUploads(nextPending);
      await openCropForFile(next);
    } else {
      // All files processed → upload
      const allToUpload = nextPending;
      setPendingUploads([]);
      await finalizeUploads(allToUpload);
    }
  };

  const handleCropSave = async (croppedFile) => {
    await proceedQueue([...pendingUploads, croppedFile]);
  };

  const handleCropSkip = async () => {
    // Reconstruct original file from data URL is unnecessary - skip means use original.
    // But we only have the data URL preview; original file was in queue closure.
    // Approach: convert data URL back to File preserving fileName.
    if (!cropPreview) {
      await proceedQueue(pendingUploads);
      return;
    }
    try {
      const res = await fetch(cropPreview.src);
      const blob = await res.blob();
      const file = new File([blob], cropPreview.fileName || 'image.jpg', { type: blob.type || 'image/jpeg' });
      await proceedQueue([...pendingUploads, file]);
    } catch (err) {
      console.error('Skip crop error:', err);
      await proceedQueue(pendingUploads);
    }
  };

  const handleCropCancel = () => {
    // Cancel entire batch
    setCropPreview(null);
    setCropQueue([]);
    setPendingUploads([]);
    toast.info('Upload cancelled');
  };

  const handleRemove = (id) => {
    const item = imageItems.find(i => i.id === id);
    if (!item) return;
    
    const newImages = value.filter(url => url !== item.url);
    onChange(newImages);
    
    // If removing main image, set first remaining as main
    if (item.url === mainImage && newImages.length > 0) {
      onMainImageChange(newImages[0]);
    } else if (newImages.length === 0) {
      onMainImageChange('');
    }
  };

  const handleSetMain = (id) => {
    const item = imageItems.find(i => i.id === id);
    if (item) {
      onMainImageChange(item.url);
    }
  };

  const handleSearchSelect = (imageUrl) => {
    if (value.length >= MAX_IMAGES) {
      toast.error(`Maximum ${MAX_IMAGES} images allowed`);
      return;
    }
    
    const newImages = [...value, imageUrl];
    onChange(newImages);
    
    if (!mainImage) {
      onMainImageChange(imageUrl);
    }
    
    toast.success('Image added from search');
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>Product Images (up to {MAX_IMAGES})</Label>
        <span className="text-xs text-slate-500">{value.length}/{MAX_IMAGES} images</span>
      </div>
      
      {/* Image Grid */}
      {value.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={imageItems.map(i => i.id)} strategy={horizontalListSortingStrategy}>
            <div className="flex flex-wrap gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
              {imageItems.map((item) => (
                <SortableImage
                  key={item.id}
                  id={item.id}
                  url={item.url}
                  isMain={item.isMain}
                  onRemove={handleRemove}
                  onSetMain={handleSetMain}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      
      {/* Upload Area */}
      {value.length < MAX_IMAGES && (
        <div className="flex gap-2">
          <div
            onClick={() => !uploading && !disabled && fileInputRef.current?.click()}
            className={`
              flex-1 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer
              transition-colors duration-200
              ${uploading ? 'border-slate-300 bg-slate-50' : 'border-slate-300 hover:border-[#0047AB] hover:bg-slate-50'}
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            {uploading ? (
              <div className="flex flex-col items-center gap-2">
                <div className="w-8 h-8 border-2 border-[#0047AB] border-t-transparent rounded-full animate-spin" />
                <span className="text-sm text-slate-500">Uploading... {uploadProgress}%</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="w-8 h-8 text-slate-400" />
                <span className="text-sm text-slate-600">Click to upload images</span>
                <span className="text-xs text-slate-400">JPG, PNG, WebP • Max 5MB each</span>
              </div>
            )}
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,image/heic,image/heif,image/avif,.heic,.heif"
              multiple
              onChange={handleFileSelect}
              disabled={uploading || disabled}
              className="hidden"
              data-testid="multi-image-file-input"
            />
          </div>
          
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowSearch(true)}
            disabled={disabled || value.length >= MAX_IMAGES}
            className="h-auto flex-col gap-1 px-4"
            data-testid="search-images-btn"
          >
            <Search className="w-5 h-5" />
            <span className="text-xs">Search</span>
          </Button>
        </div>
      )}
      
      {/* Help text */}
      <p className="text-xs text-slate-500">
        Drag to reorder. Click star to set main image. First image is shown on product cards.
      </p>
      
      {/* Image Search Modal */}
      <ImageSearchModal
        open={showSearch}
        onOpenChange={setShowSearch}
        onSelect={handleSearchSelect}
      />

      {/* Image Crop Modal */}
      <ImageCropModal
        open={Boolean(cropPreview)}
        imageSrc={cropPreview?.src}
        fileName={cropPreview?.fileName}
        onCropComplete={handleCropSave}
        onSkip={handleCropSkip}
        onCancel={handleCropCancel}
      />
    </div>
  );
};

export default MultiImageUploader;
